import {Button} from "@/components/basic/button.tsx";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/basic/dialog.tsx";
import {LanguageToggle} from "@/components/shared/selectors/LanguageToggle/LanguageToggle.tsx";
import {useTranslationWithKeys} from "@/hooks/useTranslationWithKeys";
import {useErrorHandler} from "@/hooks/useErrorHandler";
import {useAcceptTosMutation} from "@/store/generated/api";
import {useAuth} from "@/hooks/useAuth";
import {ExternalLink, FileText, Shield} from "lucide-react";
import {getTosLinks} from "../../../../utils/tosLinks";

interface TosAcceptanceDialogProps {
    open: boolean;
    onSuccess: () => void;
}

export function TosAcceptanceDialog({open, onSuccess}: TosAcceptanceDialogProps) {
    const {t, i18n} = useTranslationWithKeys();
    const {handleApiError} = useErrorHandler();
    const {logout} = useAuth();
    const [acceptTos, {isLoading}] = useAcceptTosMutation();
    const tosLinks = getTosLinks(i18n.language);

    const handleAccept = async () => {
        try {
            await acceptTos().unwrap();
            onSuccess();
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('tos.acceptError')});
        }
    };

    const handleDecline = async () => {
        await logout();
    };

    return (
        <Dialog open={open}>
            <DialogContent className="max-w-2xl" showClose={false} onPointerDownOutside={(e) => e.preventDefault()}
                           onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader>
                    <div className="flex items-center justify-center mb-4">
                        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                            <Shield className="h-8 w-8 text-primary"/>
                        </div>
                    </div>
                    <DialogTitle className="text-center text-2xl">
                        {t('tos.title')}
                    </DialogTitle>

                    <div className="absolute right-2 top-1">
                        <LanguageToggle/>
                    </div>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <p className="text-center text-muted-foreground">
                        {t('tos.description')}
                    </p>

                    <div className="space-y-4">
                        <div className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <FileText className="h-5 w-5 text-primary"/>
                                <h3 className="font-semibold">{t('tos.termsOfService')}</h3>
                            </div>
                            <a
                                href={tosLinks.termsOfService}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                {t('tos.linkTerms')}
                                <ExternalLink className="h-3 w-3"/>
                            </a>
                        </div>

                        <div className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Shield className="h-5 w-5 text-primary"/>
                                <h3 className="font-semibold">{t('tos.privacyPolicy')}</h3>
                            </div>
                            <a
                                href={tosLinks.privacyPolicy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                {t('tos.linkPrivacy')}
                                <ExternalLink className="h-3 w-3"/>
                            </a>
                        </div>

                        <div className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <FileText className="h-5 w-5 text-primary"/>
                                <h3 className="font-semibold">{t('tos.eulas')}</h3>
                            </div>
                            <a
                                href={tosLinks.eulas}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                {t('tos.linkEulas')}
                                <ExternalLink className="h-3 w-3"/>
                            </a>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={handleDecline}
                        >
                            {t('tos.decline')}
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={handleAccept}
                            disabled={isLoading}
                        >
                            {isLoading ? t('tos.accepting') : t('tos.accept')}
                        </Button>
                    </div>

                    <p className="text-xs text-center text-muted-foreground">
                        {t('tos.required')}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export type {TosAcceptanceDialogProps};
